import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./dialog";
import { FieldSet, Field, FieldGroup, FieldLabel, FieldDescription } from "./field";
import { Input } from "./input";
import { Button } from "./button";
import { Textarea } from "./textarea";
import { Upload } from "lucide-react";

interface DialogFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: string;
    description?: string;
    showAvatar?: boolean;
    showTitleRole?: boolean;
    showBio?: boolean;
    labelName?: string;
    labelNamePlaceholder?: string;
    labelTitleRole?: string;
    labelTitleRolePlaceholder?: string;
    labelTitleRoleHelper?: string;
    labelBio?: string;
    labelBioPlaceholder?: string;
    labelCancel?: string;
    labelSubmit?: string;
    children?: React.ReactNode;
    onSubmit?: (data: { name: string; title: string; bio: string }) => void | Promise<void>;
}

export const DialogForm: React.FC<DialogFormProps> = ({
    open,
    onOpenChange,
    title = "New presenter",
    description = "Added to your org roster and available across courses.",
    showAvatar = true,
    showTitleRole = true,
    showBio = true,
    labelName = "Full name",
    labelNamePlaceholder = "e.g. Severus Snape",
    labelTitleRole = "Title / role",
    labelTitleRolePlaceholder = "e.g. Potions Professor, PhD",
    labelTitleRoleHelper = "A short credential shown under their name.",
    labelBio = "Short bio",
    labelBioPlaceholder = "A sentence or two about this presenter...",
    labelCancel = "Cancel",
    labelSubmit = "Add presenter",
    children,
    onSubmit,
}) => {
    const [
        name,
        setName,
    ] = useState("");
    const [
        presenterTitle,
        setPresenterTitle,
    ] = useState("");
    const [
        bio,
        setBio,
    ] = useState("");
    const [
        submitting,
        setSubmitting,
    ] = useState(false);

    async function handleSubmit() {
        setSubmitting(true);
        try {
            await onSubmit?.({ name, title: presenterTitle, bio });
            setName("");
            setPresenterTitle("");
            setBio("");
            onOpenChange(false);
        } finally {
            setSubmitting(false);
        }
    }

    function handleCancel() {
        setName("");
        setPresenterTitle("");
        setBio("");
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <div className="flex items-start gap-4 pt-2">
                    {showAvatar && (
                        <button className="border-border hover:border-secondary flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-dashed transition-colors">
                            <Upload className="text-muted-foreground h-5 w-5" />
                        </button>
                    )}
                    <FieldSet className="flex-1">
                        <FieldGroup>
                            <Field>
                                <FieldLabel htmlFor="dialog-form-name" required>
                                    {labelName}
                                </FieldLabel>
                                <Input
                                    id="dialog-form-name"
                                    placeholder={labelNamePlaceholder}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Field>
                            {showTitleRole && (
                                <Field>
                                    <FieldLabel htmlFor="dialog-form-title" optional>
                                        {labelTitleRole}
                                    </FieldLabel>
                                    <Input
                                        id="dialog-form-title"
                                        placeholder={labelTitleRolePlaceholder}
                                        value={presenterTitle}
                                        onChange={(e) => setPresenterTitle(e.target.value)}
                                    />
                                    <FieldDescription>{labelTitleRoleHelper}</FieldDescription>
                                </Field>
                            )}
                            {showBio && (
                                <Field>
                                    <FieldLabel htmlFor="dialog-form-bio" optional>
                                        {labelBio}
                                    </FieldLabel>
                                    <Textarea
                                        id="dialog-form-bio"
                                        placeholder={labelBioPlaceholder}
                                        rows={3}
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                    />
                                </Field>
                            )}
                        </FieldGroup>
                    </FieldSet>
                </div>
                {children}
                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        {labelCancel}
                    </Button>
                    <Button disabled={!name.trim() || submitting} onClick={handleSubmit}>
                        {submitting ? "Saving…" : labelSubmit}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
